import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import { useSharedParameterStore } from '../../../shared/stores/useSharedParameterStore';
import { buildDefinitionBackedParameterTypeHints } from '../../../shared/lib/revit/wizardParameterDataTypes';
import { sanitizeRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import { mergeWithDefaultSharedParameters } from '../../../shared/lib/revit/shippedSharedParameters';
import { useVerifiedSetupRevision } from '../../../shared/stores/useUIStore';
import {
  buildDefinitionBackedMappingCatalogDiffs,
  buildMappingReadinessRevisionScopeKey,
  buildMappingRepairAttemptScopeKey,
  buildMappingFamilyRepairBlockedMessage,
  collectMappedParameterReferences,
  getMappingReadinessNameGroups,
  getMappingReadinessFamilyRepairGroups,
  resolveMappingProjectReadinessResult,
  type MappingProjectReadinessDiff,
} from '../lib/mappingProjectReadiness';
import { resolveMappingReadinessCatalogState } from '../lib/readiness/readinessCatalogState';
import {
  assessProjectParameterRequirements,
  buildSelectedMappingParameterScopeRequirements,
  reconcileScopedMissingParameters,
  selectRepairProjectBindings,
  selectUnsatisfiedRepairableRequirements,
} from '../lib/mappingReadinessFamilyExclusions';
import {
  buildUnsatisfiedProcessProjectParameterScope,
  selectMappingOnlyRepairParameterNames,
} from '../lib/mappingRepairScopes';
import type { MappingRepairActionResult } from '../lib/mappingRepairRun';
import { useMappingReadinessTypeMismatchTrace } from './useMappingReadinessTypeMismatchTrace';
import { emptyRepairParameterCounts, sortRepairKinds } from '../lib/mappingRepairProgress';
import { useOneShotMappingRepair } from './useOneShotMappingRepair';
import {
  useMappingReadinessCatalogRefresh,
  useMappingRepairCatalogSuspension,
  useStableMappingRepairSignature,
} from './useMappingReadinessCatalogRefresh';
import { useMappingReadinessDiagnostics } from './useMappingReadinessDiagnostics';
import { useMappingScopedRequirementAssessment } from './useMappingScopedRequirementAssessment';
import { useMappingReadinessSnapshot } from './useMappingReadinessSnapshot';
import { useMappingFamilyRequirementBackendAssessment } from './useMappingFamilyRequirementBackendAssessment';
import type {
  MappingProjectReadinessHookResult,
  UseMappingProjectReadinessArgs,
} from './mappingProjectReadinessHookTypes';
export type { MappingProjectReadinessResult } from '../lib/mappingProjectReadiness';
export type { MappingProjectReadinessHookResult } from './mappingProjectReadinessHookTypes';
export function useMappingProjectReadiness({
  activeUserId,
  objectTypes,
  menuConfig,
  connectorConfig,
  boltConfig,
  processConfig,
  additionalCategoriesConfig,
  processConditionsEditorState,
  projectPropertiesEditorState,
  selectedProfileKey,
  enabled = true,
  autoRepair = true,
  autoRepairBlockedMessage = null,
}: UseMappingProjectReadinessArgs): MappingProjectReadinessHookResult {
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const runtimeAvailable = isGrpcRuntimeAvailable();
  const hostRuntimeAvailable = runtimeAvailable;
  const projectKey = activeDocumentInfo.projectKey;
  const normalizedProjectKey = useMemo(() => normalizeMappingProjectKey(projectKey), [projectKey]);
  const modelVersion = activeDocumentInfo.modelVersion;
  const verifiedRevision = useVerifiedSetupRevision(selectedProfileKey);
  const catalogRefreshSuspendedRef = useRef({ active: false, projectKey: null as string | null });
  const references = useMemo(
    () =>
      collectMappedParameterReferences({
        objectTypes,
        menuConfig,
        connectorConfig,
        boltConfig,
        processConfig,
        additionalCategoriesConfig,
        processConditionsEditorState,
        projectPropertiesEditorState,
      }),
    [
      additionalCategoriesConfig,
      boltConfig,
      connectorConfig,
      menuConfig,
      objectTypes,
      processConfig,
      processConditionsEditorState,
      projectPropertiesEditorState,
    ]
  );
  const sharedParameters = useSharedParameterStore((state) => state.parsedParameters);
  const sharedParameterDefinitions = useMemo(
    () => mergeWithDefaultSharedParameters(sharedParameters),
    [sharedParameters]
  );
  const inspectionRequirements = useMemo(
    () => buildSelectedMappingParameterScopeRequirements(
      { menuConfig, connectorConfig, boltConfig, additionalCategoriesConfig },
      references,
      references.map((reference) => reference.name),
      buildDefinitionBackedParameterTypeHints(
        references.map((reference) => reference.name),
        sharedParameterDefinitions
      )
    ),
    [
      additionalCategoriesConfig,
      boltConfig,
      connectorConfig,
      menuConfig,
      references,
      sharedParameterDefinitions,
    ]
  );
  const readinessSnapshot = useMappingReadinessSnapshot({
    enabled,
    runtimeAvailable,
    projectKey: normalizedProjectKey,
    modelVersion,
    evidenceRevision: verifiedRevision,
    requirements: inspectionRequirements,
    refreshSuspendedRef: catalogRefreshSuspendedRef,
  });
  const refreshReadinessSnapshot = readinessSnapshot.refresh;
  const { catalogDiff, readinessDiff: baseDiff } = useMemo(
    () => buildDefinitionBackedMappingCatalogDiffs(
      references,
      {
        projectParameterNames: readinessSnapshot.parameterNames,
        sampledParameterNames: readinessSnapshot.sampledParameterNames,
      },
      readinessSnapshot.projectDescriptors,
      sharedParameterDefinitions
    ),
    [
      readinessSnapshot.parameterNames,
      readinessSnapshot.projectDescriptors,
      references,
      readinessSnapshot.sampledParameterNames,
      sharedParameterDefinitions,
    ]
  );
  useMappingReadinessTypeMismatchTrace({
    projectKey: normalizedProjectKey,
    modelVersion,
    catalogDiff,
    baseDiff,
  });
  const scopeRequirements = useMemo(
    () =>
      buildSelectedMappingParameterScopeRequirements(
        {
          menuConfig,
          connectorConfig,
          boltConfig,
          additionalCategoriesConfig,
        },
        baseDiff.references,
        baseDiff.missing
          .filter((reference) => reference.repairKinds.includes('mapping'))
          .map((reference) => reference.name),
        buildDefinitionBackedParameterTypeHints(
          baseDiff.references.map((reference) => reference.name),
          sharedParameterDefinitions
        )
      ),
    [
      additionalCategoriesConfig,
      baseDiff.references,
      baseDiff.missing,
      boltConfig,
      connectorConfig,
      menuConfig,
      sharedParameterDefinitions,
    ]
  );
  // Model revisions refresh evidence; scope/setup revisions describe another repair.
  const revisionSignature = useMemo(
    () =>
      buildMappingReadinessRevisionScopeKey({
        userId: activeUserId,
        projectKey: normalizedProjectKey,
        profileKey: selectedProfileKey,
        requirementKeys: scopeRequirements.map((requirement) => requirement.key),
        verifiedRevision,
      }),
    [activeUserId, normalizedProjectKey, scopeRequirements, selectedProfileKey, verifiedRevision]
  );
  const signature = useStableMappingRepairSignature(
    revisionSignature,
    normalizedProjectKey,
    catalogRefreshSuspendedRef
  );
  const repairAttemptScope = buildMappingRepairAttemptScopeKey({
    userId: activeUserId, projectKey: normalizedProjectKey, profileKey: selectedProfileKey,
    requirementKeys: inspectionRequirements.map((requirement) => requirement.key), verifiedRevision,
  });
  const {
    familyRequirements,
    assessments: familyAssessments,
    satisfiedNames: familySatisfiedNames,
    isReady: backendFamilyAssessmentReady,
    error: backendFamilyAssessmentError,
    refresh: refreshBackendFamilyAssessment,
  } = useMappingFamilyRequirementBackendAssessment({
    enabled,
    runtimeAvailable,
    projectKey: normalizedProjectKey,
    modelVersion,
    requirements: scopeRequirements,
    refreshSuspendedRef: catalogRefreshSuspendedRef,
  });
  // Snapshot and backend family verdicts refresh together: verification after a
  // repair re-reads both, so the same engine decides before and after repair.
  const refreshReadinessEvidence = useCallback(async () => {
    const snapshot = await refreshReadinessSnapshot();
    if (familyRequirements.length > 0) {
      // The assessment error is already recorded in hook state and drives the
      // blocked verdict; only the snapshot error propagates to callers.
      await refreshBackendFamilyAssessment().catch(() => undefined);
    }
    return snapshot;
  }, [familyRequirements.length, refreshBackendFamilyAssessment, refreshReadinessSnapshot]);
  const { refreshReadinessCatalogs, revisionRefreshPending } = useMappingReadinessCatalogRefresh({
    verifiedRevision,
    enabled: enabled && runtimeAvailable,
    projectKey: normalizedProjectKey,
    refreshCatalog: refreshReadinessEvidence,
    refreshSuspendedRef: catalogRefreshSuspendedRef,
  });
  const {
    conflictMessage: scopeConflictMessage,
    liveDiff,
    projectAssessments: projectScopeSatisfaction,
    unsatisfiedRepairableRequirements,
  } = useMappingScopedRequirementAssessment({
    requirements: scopeRequirements,
    baseDiff,
    projectDescriptors: readinessSnapshot.projectDescriptors,
    sampledParameterNames: readinessSnapshot.sampledParameterNames,
    familyAssessments,
    familySatisfiedNames,
  });
  const [frozenRepairDiff, setFrozenRepairDiff] = useState<MappingProjectReadinessDiff | null>(null);
  const diff = frozenRepairDiff ?? liveDiff;
  const names = useMemo(() => getMappingReadinessNameGroups(diff), [diff]);
  const mappedParameterNames = useMemo(
    () => selectMappingOnlyRepairParameterNames(diff),
    [diff]
  );
  const processProjectParameterScope = useMemo(
    () => buildUnsatisfiedProcessProjectParameterScope(
      scopeRequirements,
      projectScopeSatisfaction
    ),
    [projectScopeSatisfaction, scopeRequirements]
  );
  const familyRepairGroups = useMemo(
    () =>
      getMappingReadinessFamilyRepairGroups({
        objectTypes,
        menuConfig,
        connectorConfig,
        boltConfig,
        processConfig,
        additionalCategoriesConfig,
        processConditionsEditorState,
        projectPropertiesEditorState,
      }, diff),
    [
      additionalCategoriesConfig,
      boltConfig,
      connectorConfig,
      diff,
      menuConfig,
      objectTypes,
      processConfig,
      processConditionsEditorState,
      projectPropertiesEditorState,
    ]
  );
  const repairKinds = useMemo(
    () => sortRepairKinds(Array.from(new Set(
      unsatisfiedRepairableRequirements.map((requirement) => requirement.repairKind)
    ))),
    [unsatisfiedRepairableRequirements]
  );
  const repairParameterCounts = useMemo(() => {
    const counts = { ...emptyRepairParameterCounts };
    unsatisfiedRepairableRequirements.forEach((requirement) => {
      counts[requirement.repairKind] += 1;
    });
    return counts;
  }, [unsatisfiedRepairableRequirements]);
  const repairProjectBindings = useMemo(
    () => selectRepairProjectBindings(unsatisfiedRepairableRequirements),
    [unsatisfiedRepairableRequirements]
  );
  // A failed backend family assessment is surfaced exactly like a snapshot
  // failure: the catalog counts as mismatched and unavailable, so readiness
  // blocks with the backend error instead of silently deriving family
  // verdicts from the lossy snapshot DTOs.
  const parametersError = readinessSnapshot.error ?? backendFamilyAssessmentError;
  const backendFamilyAssessmentPending =
    familyRequirements.length > 0 &&
    !backendFamilyAssessmentError &&
    !backendFamilyAssessmentReady;
  const { waitingForSampledCatalog, catalogLoading, catalogReady, repairStartHold, catalogUnavailable } =
    resolveMappingReadinessCatalogState({
      projectCatalogLoading: readinessSnapshot.isLoading || backendFamilyAssessmentPending,
      projectCatalogLoaded: readinessSnapshot.isReady,
      projectCatalogMatchesProject: readinessSnapshot.isReady && !backendFamilyAssessmentError,
      projectCatalogHasError: Boolean(parametersError),
      autoRepair,
      runtimeAvailable,
      hostRuntimeAvailable,
      repairableCount: names.repairable.length,
      sampledCatalogReady: readinessSnapshot.isReady,
      referenceCount: references.length,
      familySatisfactionApplies: familyRequirements.length > 0,
      familySatisfactionReady: readinessSnapshot.isReady && backendFamilyAssessmentReady,
      missingCount: baseDiff.missing.length,
    });
  const refreshFamilyRepairKinds = useCallback(
    async () => {
      const refreshed = await refreshBackendFamilyAssessment();
      return sortRepairKinds(Array.from(new Set(
        selectUnsatisfiedRepairableRequirements(
          familyRequirements,
          refreshed.assessments
        ).map((requirement) => requirement.repairKind)
      )));
    },
    [familyRequirements, refreshBackendFamilyAssessment]
  );
  const verifyRepairParameters = useCallback(async (_repairActionResults?: MappingRepairActionResult[]) => {
    const refreshed = await refreshReadinessSnapshot();
    // Verification re-fetches the same backend assessment that produced the
    // pre-repair verdict, so a requirement verifies satisfied exactly when the
    // host reports it satisfied — no FE-side reconciliation of repair-reported
    // evidence against the lossy snapshot DTOs.
    const refreshedFamilyAssessments = familyRequirements.length > 0
      ? (await refreshBackendFamilyAssessment()).assessments
      : [];
    const verificationScopeSatisfaction = [
      ...assessProjectParameterRequirements(
        scopeRequirements,
        refreshed.projectDescriptors,
        refreshed.sampledParameterNames
      ),
      ...refreshedFamilyAssessments,
    ];
    const scopedVerificationDiff = reconcileScopedMissingParameters(
      {
        references: diff.repairable,
        missing: [],
        repairable: [],
        blocked: [],
      },
      scopeRequirements,
      verificationScopeSatisfaction
    );
    const missingParameterNames = sanitizeRevitParameterNames(
      scopedVerificationDiff.missing.map((reference) => reference.name)
    );
    if (missingParameterNames.length === 0) {
      return {
        success: true,
        missingParameterNames: [],
        message: `Revit confirmed all ${scopeRequirements.length} scoped Mapping requirements.`,
      };
    }
    const count = missingParameterNames.length;
    // A family-availability gap (zero families match the object-type target)
    // can never be satisfied by a project-catalog repair, so reporting those
    // parameters as plain missing project parameters mislabels the block and
    // drives repair loops after bindings that cannot satisfy a family scope.
    // Name the family block; keep genuine project-catalog misses in the
    // not-found suffix.
    const verificationAssessments = verificationScopeSatisfaction.filter(
      (assessment) => assessment.satisfied !== true
    );
    const familyAvailabilityGap = verificationAssessments.some(
      (assessment) =>
        assessment.targetKind === 'familyObjectTypes' &&
        assessment.matchingEntityCount === 0
    );
    if (familyAvailabilityGap) {
      const familyBlockedMessage = buildMappingFamilyRepairBlockedMessage({
        familyGroups: familyRepairGroups,
        quickFillAttempted: true,
        missingParameterNames: sanitizeRevitParameterNames(
          scopeRequirements
            .filter((requirement) =>
              verificationAssessments.some(
                (assessment) =>
                  assessment.requirementKey === requirement.key &&
                  assessment.targetKind === 'projectCategories'
              )
            )
            .map((requirement) => requirement.parameterName)
        ),
      });
      if (familyBlockedMessage) {
        return {
          success: false,
          missingParameterNames,
          message: familyBlockedMessage,
        };
      }
    }
    return {
      success: false,
      missingParameterNames,
      message: `${count} scoped Revit parameter requirement${count === 1 ? ' is' : 's are'} still missing: ${missingParameterNames.join(', ')}.`,
    };
  }, [
    diff.repairable,
    familyRequirements,
    familyRepairGroups,
    refreshBackendFamilyAssessment,
    refreshReadinessSnapshot,
    scopeRequirements,
  ]);
  const handleRepairInFlightChange = useMappingRepairCatalogSuspension({
    attemptScope: repairAttemptScope, projectKey: normalizedProjectKey,
    refreshCatalog: refreshReadinessEvidence, refreshSuspendedRef: catalogRefreshSuspendedRef,
  });
  const repair = useOneShotMappingRepair({
    activeUserId,
    enabled,
    autoRepair,
    projectKey: normalizedProjectKey,
    profileKey: selectedProfileKey,
    modelVersion,
    menuConfig,
    connectorConfig,
    objectTypes,
    catalogReady,
    catalogLoading: catalogLoading || repairStartHold || revisionRefreshPending,
    repairableCount: names.repairable.length,
    repairableParameterNames: names.repairable,
    missingParameterNames: names.missing,
    mappedParameterNames,
    processProjectParameterScope,
    repairProjectBindings,
    repairKinds,
    repairParameterCounts,
    familyRepairGroups,
    signature,
    attemptScope: repairAttemptScope,
    resolveFamilyRepairKindsAfterClassification: scopeRequirements.some(
      (requirement) => requirement.target.kind === 'familyObjectTypes'
    ) ? refreshFamilyRepairKinds : undefined,
    verifyRepairParameters,
    onRepairInFlightChange: handleRepairInFlightChange,
  });
  const { clearRepairAttempt } = repair;
  const retry = useCallback(() => { clearRepairAttempt(); refreshReadinessCatalogs(); }, [
    clearRepairAttempt, refreshReadinessCatalogs,
  ]);
  // Pin the repair-start diff; verification refreshes both catalogs before release.
  useEffect(() => {
    if (repair.isRepairing) {
      setFrozenRepairDiff((current) => current ?? liveDiff);
    } else {
      setFrozenRepairDiff(null);
    }
  }, [liveDiff, repair.isRepairing]);
  // A family-availability gap (zero families match the object-type target)
  // is not a project-catalog gap: the parameters live in families, so
  // reporting them as "missing in this project" mislabels the block and
  // sends repair loops after project bindings that can never satisfy a
  // family scope.
  const familyScopeBlockedMessage = useMemo(() => {
    if (unsatisfiedRepairableRequirements.length === 0 || familyRepairGroups.length === 0) {
      return null;
    }
    const assessmentByKey = new Map(
      [...projectScopeSatisfaction, ...familyAssessments]
        .map((assessment) => [assessment.requirementKey, assessment])
    );
    const familyAvailabilityGap = unsatisfiedRepairableRequirements.some(
      (requirement) =>
        requirement.target.kind === 'familyObjectTypes' &&
        (assessmentByKey.get(requirement.key)?.matchingEntityCount ?? -1) === 0
    );
    if (!familyAvailabilityGap) {
      return null;
    }
    return buildMappingFamilyRepairBlockedMessage({
      familyGroups: familyRepairGroups,
      quickFillAttempted: repair.hasAttemptedRepair,
      missingParameterNames: sanitizeRevitParameterNames(
        unsatisfiedRepairableRequirements
          .filter((requirement) => requirement.target.kind === 'projectCategories')
          .map((requirement) => requirement.parameterName)
      ),
    });
  }, [
    familyAssessments,
    familyRepairGroups,
    projectScopeSatisfaction,
    repair.hasAttemptedRepair,
    unsatisfiedRepairableRequirements,
  ]);
  const readinessResult = resolveMappingProjectReadinessResult({
    enabled,
    hasReferences: references.length > 0,
    hostRuntimeAvailable,
    projectKey: normalizedProjectKey,
    catalogReady,
    catalogLoading: catalogLoading || revisionRefreshPending,
    catalogUnavailable,
    parametersError,
    names,
    autoRepair,
    autoRepairBlockedMessage,
    familyRepairBlockedMessage: familyScopeBlockedMessage,
    scopeConflictMessage,
    hasAttemptedRepair: repair.hasAttemptedRepair,
    isRepairing: repair.isRepairing,
    repairErrorMessage: repair.repairErrorMessage,
    retry,
  });
  useMappingReadinessDiagnostics({
    projectKey,
    normalizedProjectKey,
    modelVersion,
    selectedProfileKey,
    signature,
    readinessResult,
    referenceCount: references.length,
    catalogReady,
    catalogLoading: catalogLoading || revisionRefreshPending,
    waitingForSampledCatalog,
    catalogUnavailable,
    runtimeAvailable,
    hostRuntimeAvailable,
    projectCatalog: {
      isLoaded: readinessSnapshot.isReady,
      matchesProject: readinessSnapshot.isReady,
      error: readinessSnapshot.error,
      parameterNames: readinessSnapshot.parameterNames,
    },
    sampledCatalog: {
      isReady: readinessSnapshot.isReady,
      isLoading: readinessSnapshot.isLoading,
      names: readinessSnapshot.sampledParameterNames,
    },
    familySatisfaction: {
      applies: familyRequirements.length > 0,
      isReady: readinessSnapshot.isReady && backendFamilyAssessmentReady,
      satisfiedCount: familySatisfiedNames.length,
    },
    repairKinds,
    repair,
  });
  return {
    ...readinessResult,
    hasAttemptedRepair: repair.hasAttemptedRepair,
    isRepairing: repair.isRepairing,
    repairErrorMessage: repair.repairErrorMessage,
    repairAssessmentReady: catalogReady && !catalogLoading && !repairStartHold && !revisionRefreshPending,
    operationProgressModel: repair.operationProgressModel,
    mappedParameterNames,
    repairKinds,
    refresh: refreshReadinessCatalogs,
  };
}
